/* ecomific-region-variant-select.js  ·  UD live cascade + hide-unavailable + sold-out card cleanup  [ud-cascade-v4]
 *
 * For each region-aware product CARD, per the visitor's region:
 *  1. Walk variants biggest -> smallest, checking each against the same
 *     /inventory endpoint the availability controller uses.
 *  2. HIDE the options that are sold out in the region (so shoppers can't pick a
 *     variant that isn't available to them), and SELECT the largest one that IS
 *     in stock.
 *  3. If NO variant is in stock in the region, collapse the picker to a single
 *     non-interactive "Sold Out" option (keeping a real variant id on it so the
 *     notify signup still targets a variant) and let the controller mark the card
 *     sold-out and show the notify button. => the picker never looks purchasable
 *     on a product that is fully sold out per region.
 *
 * Safety:
 *  - Falls back to the old metafield selection if the endpoint is unreachable.
 *  - Suppresses the notify button while a card is resolving (no sold-out flash).
 *  - "unknown" (endpoint error for a variant) is left visible, never hidden.
 *  - Global cap on concurrent /inventory calls; results cached per variant+region.
 *  - On region change, all options are un-hidden and the card is re-resolved.
 *
 * Scope: collection/home CARDS ([data-ecomific-product]) only. PDP unchanged.
 */
(function () {
  'use strict';

  if (window.__ECOMIFIC_REGION_VARIANT_SELECT__) return;
  window.__ECOMIFIC_REGION_VARIANT_SELECT__ = true;

  var REGION_KEY = 'ecomific_region';
  var TIMEOUT_MS = 4500;
  var MAX_INFLIGHT = 4;

  var availCache = {};   // "variantId|region" -> true|false
  var inFlight = {};     // "variantId|region" -> Promise<bool|null>
  var fetchQueue = [];
  var running = 0;

  // --- flicker guard: hide the notify button while a card is still resolving ---
  try {
    var style = document.createElement('style');
    style.setAttribute('data-ecomific-cascade-style', '1');
    style.textContent =
      '[data-ecomific-cascade="pending"] .grid-product__notify-me,' +
      '[data-ecomific-cascade="pending"] .klaviyo-bis-trigger{display:none !important;}';
    (document.head || document.documentElement).appendChild(style);
  } catch (e) {}

  function getCookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }
  function getRegion() {
    try { var ls = localStorage.getItem(REGION_KEY); if (ls) return ls.trim().toLowerCase(); } catch (e) {}
    return getCookie(REGION_KEY).trim().toLowerCase();
  }
  function getEndpoint() {
    var cfg = (window.ecomificAvailabilityConfig && typeof window.ecomificAvailabilityConfig === 'object')
      ? window.ecomificAvailabilityConfig : {};
    var base = cfg.endpointBase || cfg.inventoryApiBase || '';
    if (!base || !/^https?:\/\//i.test(base)) return '';
    var head = String(base).split(/[?#]/)[0].replace(/\/+$/, '');
    if (!/\/inventory$/.test(head)) head += '/inventory';
    return head;
  }

  // Global concurrency cap on /inventory calls.
  function drain() {
    while (running < MAX_INFLIGHT && fetchQueue.length) {
      var job = fetchQueue.shift();
      running++;
      job().then(function () { running--; drain(); });
    }
  }
  function throttled(task) {
    return new Promise(function (resolve) {
      fetchQueue.push(function () { return task().then(resolve, function () { resolve(null); }); });
      drain();
    });
  }

  // Resolves true (in stock in region), false (out), or null (unknown/error).
  function isAvailable(variantId, region, endpoint) {
    var key = variantId + '|' + region;
    if (availCache[key] !== undefined) return Promise.resolve(availCache[key]);
    if (inFlight[key]) return inFlight[key];
    var p = throttled(function () {
      var url = endpoint + '?variant_id=' + encodeURIComponent(variantId) + '&region=' + encodeURIComponent(region);
      var ac = ('AbortController' in window) ? new AbortController() : null;
      var timer = ac ? setTimeout(function () { try { ac.abort(); } catch (e) {} }, TIMEOUT_MS) : null;
      return fetch(url, { credentials: 'omit', signal: ac ? ac.signal : undefined })
        .then(function (r) { return r.json(); })
        .then(function (j) { var ok = !!(j && j.ok === true && j.available === true); availCache[key] = ok; if (timer) clearTimeout(timer); return ok; })
        .catch(function () { if (timer) clearTimeout(timer); return null; });
    }).then(function (v) { delete inFlight[key]; return v; });
    inFlight[key] = p;
    return p;
  }

  function selectOption(select, variantId) {
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value == variantId) {
        if (select.selectedIndex !== i) {
          select.selectedIndex = i;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return true;
      }
    }
    return false;
  }
  // Snapshot the original options once so we can always restore the full list
  // (e.g. on region change, or when a product turns out fully sold out).
  function snapshot(card, select) {
    if (!card.__ecomificOrigOpts) card.__ecomificOrigOpts = Array.prototype.slice.call(select.options);
  }
  function restoreOptions(card, select) {
    if (!card.__ecomificOrigOpts) return;
    while (select.options.length) select.remove(0);
    for (var i = 0; i < card.__ecomificOrigOpts.length; i++) select.appendChild(card.__ecomificOrigOpts[i]);
    select.disabled = false; // undo the sold-out collapse below
  }
  function removeOption(select, opt) {
    if (opt && opt.parentNode === select) select.removeChild(opt);
  }
  // Localized "Sold Out" label: reuse the one the theme already rendered on the
  // card's Add-to-Cart button (data-ecomific-sold-out-text), so EN/FR stay correct.
  function soldOutLabel(card) {
    var el = card.querySelector('[data-ecomific-sold-out-text]');
    var t = (el && el.getAttribute('data-ecomific-sold-out-text') || '').trim();
    return t || 'Sold Out';
  }
  // Collapse the picker to a single, non-interactive "Sold Out" entry. Keeps a real
  // variant id as the option value so the Klaviyo notify signup still has a variant to
  // target. The original options live in card.__ecomificOrigOpts and are put back by
  // restoreOptions on region change.
  function showSoldOut(card, select) {
    var origOpts = card.__ecomificOrigOpts;
    var firstId = (origOpts && origOpts[0]) ? origOpts[0].value : '';
    while (select.options.length) select.remove(0);
    var o = document.createElement('option');
    o.value = firstId;
    o.textContent = soldOutLabel(card);
    o.selected = true;
    select.appendChild(o);
    select.disabled = true;
  }
  function metafieldFallback(card, region, select) {
    try {
      var raw = card.getAttribute('data-region-variants');
      if (!raw) return;
      var id = JSON.parse(raw)[region];
      if (id) selectOption(select, id);
    } catch (e) {}
  }

  function resolveCard(card, region, endpoint) {
    if (card.__ecomificCascadeRegion === region) return;
    card.__ecomificCascadeRegion = region;

    var select = card.querySelector('select[name="id"]');
    if (!select || select.options.length < 1) { card.setAttribute('data-ecomific-cascade', 'done'); return; }
    // Leave "Coming Soon" placeholders alone: the theme renders a single non-purchasable
    // "Coming Soon" option for pre-launch products, and collapsing that to sold-out is wrong.
    if (select.options.length === 1 && /coming soon/i.test(select.options[0].textContent || '')) {
      card.setAttribute('data-ecomific-cascade', 'done');
      return;
    }

    snapshot(card, select);
    var opts = Array.prototype.slice.call(select.options); // biggest-first
    card.setAttribute('data-ecomific-cascade', 'pending');

    var idx = 0, selected = false, removedAny = false, sawUnknown = false;
    function step() {
      if (idx >= opts.length) {
        if (!selected) {
          if (sawUnknown) {
            // Endpoint was flaky for one or more variants -> don't assert sold-out.
            // Restore the real list and fall back to the metafield selection.
            if (removedAny) restoreOptions(card, select);
            metafieldFallback(card, region, select);
          } else {
            // Confident: nothing is available in this region. Collapse the picker to a
            // single "Sold Out" entry; the controller marks the card sold-out and shows notify.
            showSoldOut(card, select);
          }
        }
        card.setAttribute('data-ecomific-cascade', 'done');
        return;
      }
      var opt = opts[idx++];
      isAvailable(opt.value, region, endpoint).then(function (av) {
        if (av === false) { removeOption(select, opt); removedAny = true; }
        else if (av === true) {
          if (!selected) { selectOption(select, opt.value); selected = true; card.setAttribute('data-ecomific-cascade', 'done'); }
        } else { sawUnknown = true; } // unknown: leave visible
        step();
      });
    }
    step();
  }

  function run(root) {
    var region = getRegion();
    if (region !== 'east' && region !== 'west') return;
    var endpoint = getEndpoint();
    var cards = (root || document).querySelectorAll('[data-ecomific-product]');
    if (!endpoint) { // no endpoint: preserve old behaviour
      for (var i = 0; i < cards.length; i++) {
        var sel = cards[i].querySelector('select[name="id"]');
        if (sel) metafieldFallback(cards[i], region, sel);
      }
      return;
    }
    for (var k = 0; k < cards.length; k++) resolveCard(cards[k], region, endpoint);
  }

  window.addEventListener('ecomific:region:ready', function () { run(); });
  window.addEventListener('ecomific:region:changed', function () {
    var cards = document.querySelectorAll('[data-ecomific-product]');
    for (var i = 0; i < cards.length; i++) {
      cards[i].__ecomificCascadeRegion = null;
      var sel = cards[i].querySelector('select[name="id"]');
      if (sel) restoreOptions(cards[i], sel);
    }
    run();
  });

  if (typeof MutationObserver !== 'undefined') {
    var obs = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        if (!added) continue;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType === 1 &&
              ((n.matches && n.matches('[data-ecomific-product]')) ||
               (n.querySelector && n.querySelector('[data-ecomific-product]')))) {
            run(n.parentElement || document);
            return;
          }
        }
      }
    });
    if (document.body) obs.observe(document.body, { childList: true, subtree: true });
    else document.addEventListener('DOMContentLoaded', function () { obs.observe(document.body, { childList: true, subtree: true }); });
  }

  function onReady() { run(); }
  document.addEventListener('DOMContentLoaded', onReady);
  if (document.readyState !== 'loading') onReady();
})();
